import type React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faCirclePlus,
  faCommentDots,
  faLayerGroup,
  faMicroscope,
} from "@fortawesome/sharp-solid-svg-icons"
import { Box, Link, Stack, styled, Tooltip, Typography } from "@mui/material"
import type { GridColDef, GridRowClassNameParams } from "@mui/x-data-grid-pro"
import { gridClasses } from "@mui/x-data-grid-pro"
import type {
  BiothreatSignal,
  GdeltSignalMember,
  NwssMeta,
} from "@phc/common/gen/threatAnalyticsGen"
import { formatDateOnly, formatLocalDateTime } from "../../utils/dates"
import { ROUTES } from "../../utils/constants"
import { extraColors, theme as appTheme } from "../../utils/theme"
import { sharedGridSx } from "../AlertCuration/AlertCurationShared"
import { ActionCell } from "../AlertCuration/AlertCurationTableColumns/SharedAlertTableColumns"
import {
  SOURCE_TYPE_TO_ICON,
  SOURCE_TYPE_TO_LABEL,
} from "../AlertCuration/AlertCurationUtils"
import type { BiothreatSignal as AlertCurationBiothreatSignal } from "../AlertCuration/AlertCurationContext"
import { buildBiothreatSignalsContextMessage } from "../Chat/prompts"
import type { NewChatMessage } from "../Chat/useChatSession"
import { GdeltMemberListItem } from "./GdeltMemberListItem"
import type { PrecomputedBiothreatSignal } from "./types"
import {
  formatNwssResolvedLocation,
  formatNwssSignalDescription,
  PRECOMPUTED_WASTEWATER_SOURCE_LABEL,
} from "./nwssDisplayFormat"
import { formatBiothreatActiveDateRange } from "./biothreatActiveDateRange"
import { compareNwssCounties, compareNwssSites } from "./useNwssSignalSamples"
const precomputedSignalDescription = (
  row: PrecomputedBiothreatSignal
): string =>
  row.source === "nwss" ? formatNwssSignalDescription(row.title) : row.title

export const PRECOMPUTED_DRAWER_ACTIVE_ROW_CLASS = "drawer-active"

export const getPrecomputedDrawerActiveRowClassName = (
  params: GridRowClassNameParams<PrecomputedBiothreatSignal>,
  activeRowId: string | null | undefined
): string =>
  activeRowId && params.row.id === activeRowId
    ? PRECOMPUTED_DRAWER_ACTIVE_ROW_CLASS
    : ""

export const precomputedGridSx = {
  ...sharedGridSx,
  // BaseGrid zeros header padding on `.MuiDataGrid-root`; match cell inset so
  // left-aligned headers line up with row values.
  [`& .${gridClasses.root} .${gridClasses.columnHeader}`]: {
    padding: "0 8px",
  },
  [`& .${gridClasses.root} .${gridClasses.cell}`]: {
    padding: "0 8px",
  },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--alignLeft"]}`]:
    {
      position: "relative",
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--alignLeft"]} .${gridClasses.iconButtonContainer}`]:
    {
      position: "absolute",
      right: 8,
      width: "auto",
    },
  [`& .${gridClasses.row}.${PRECOMPUTED_DRAWER_ACTIVE_ROW_CLASS} .${gridClasses.cell}`]:
    {
      backgroundColor: extraColors.translucentActive,
    },
  [`& .${gridClasses.columnHeader}:not(.${gridClasses["columnHeader--alignLeft"]}) .${gridClasses.columnHeaderTitleContainer}`]:
    {
      justifyContent: "center",
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--alignLeft"]} .${gridClasses.columnHeaderTitleContainer}`]:
    {
      justifyContent: "flex-start",
    },
  // Sorted header sits above its neighbors AND lets its inner containers
  // overflow, so the rank badge isn't clipped by the column's width/overflow.
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]}`]: {
    position: "relative",
    zIndex: 3,
    overflow: "visible",
  },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]} .${gridClasses.columnHeaderDraggableContainer}`]:
    {
      overflow: "visible",
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]} .${gridClasses.columnHeaderTitleContainer}`]:
    {
      overflow: "visible",
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]} .${gridClasses.iconButtonContainer}`]:
    {
      visibility: "visible",
      width: "auto",
      overflow: "visible",
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]} .${gridClasses.sortIcon}`]:
    {
      opacity: 1,
      color: extraColors.status.navyLight,
    },
  [`& .${gridClasses.columnHeader}.${gridClasses["columnHeader--sorted"]} .MuiBadge-badge`]:
    {
      fontSize: "0.55rem",
      fontWeight: 800,
      minWidth: 14,
      height: 14,
      padding: "0 3px",
      backgroundColor: extraColors.status.navyLight,
      color: extraColors.white,
      zIndex: 4,
    },
  [`& .${gridClasses.cell}:not(.${gridClasses["cell--textLeft"]})`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  [`& .${gridClasses.cell}.${gridClasses["cell--textLeft"]}`]: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
  },
}

const CLUSTER_MEMBERS_TOOLTIP_LIMIT = 20

const ClusterTooltipContainer = styled(Box)(() => ({
  padding: 8,
  maxWidth: 450,
  maxHeight: 400,
  overflowY: "auto",
}))

const ClusterTooltipHeader = styled(Typography)(() => ({
  fontWeight: 800,
  color: extraColors.translucent50,
  marginBottom: 12,
  display: "block",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
}))

const ClusterTooltipSubheader = styled(Typography)(() => ({
  fontWeight: 700,
  color: extraColors.translucent50,
  marginBottom: 8,
  display: "block",
  fontSize: "0.65rem",
  letterSpacing: "0.04em",
}))

export const NwssSourceTooltip: React.FC<{
  meta: NwssMeta
  sourceCount: number
}> = ({ meta, sourceCount }) => {
  const isStateLevel = meta.aggregation === "state"
  const items = isStateLevel
    ? (meta.counties ?? []).slice().sort(compareNwssCounties)
    : [...meta.site_ids].sort(compareNwssSites)
  const header = isStateLevel ? "Reporting counties" : "Reporting site IDs"

  return (
    <ClusterTooltipContainer>
      <ClusterTooltipHeader variant="caption">
        {header} ({sourceCount})
      </ClusterTooltipHeader>
      {items.length > 0 ? (
        <Stack spacing={0.5}>
          {items.map(item => (
            <Typography
              key={item}
              variant="caption"
              sx={{ color: extraColors.white, display: "block" }}
            >
              {item}
            </Typography>
          ))}
        </Stack>
      ) : (
        <Typography variant="caption" sx={{ color: extraColors.translucent50 }}>
          No {isStateLevel ? "counties" : "sites"} listed for this event.
        </Typography>
      )}
    </ClusterTooltipContainer>
  )
}

export const ClusterMembersTooltip: React.FC<{
  clusterCount: number
  members: GdeltSignalMember[]
}> = ({ clusterCount, members }) => {
  const displayed = members
    .slice(0, CLUSTER_MEMBERS_TOOLTIP_LIMIT)
    .filter(member => member.title.trim())

  return (
    <ClusterTooltipContainer>
      <ClusterTooltipHeader variant="caption">
        Cluster Members ({clusterCount})
      </ClusterTooltipHeader>
      {displayed.length > 0 && (
        <ClusterTooltipSubheader variant="caption">
          {displayed.length === 1
            ? "Top Unique Member"
            : `Top ${displayed.length} unique members`}
        </ClusterTooltipSubheader>
      )}
      <Stack spacing={1}>
        {displayed.map(member => (
          <GdeltMemberListItem
            key={member.id}
            title={member.title}
            sourceName={member.source_name}
            publishTime={member.publish_time}
            url={member.url}
            onLinkClick={e => e.stopPropagation()}
          />
        ))}
      </Stack>
    </ClusterTooltipContainer>
  )
}

export const ImpactScoreBadge = styled(Box, {
  shouldForwardProp: prop => prop !== "score",
})<{ score: number }>(({ score }) => ({
  ...appTheme.typography.small1Bold,
  backgroundColor:
    score >= 70
      ? "rgba(41, 209, 159, 0.12)"
      : score >= 40
        ? "rgba(41, 209, 159, 0.08)"
        : extraColors.translucent5,
  color: score >= 40 ? extraColors.status.greenLight : extraColors.white,
  padding: "2px 0",
  borderRadius: "4px",
  textAlign: "center",
  width: "42px",
  height: "20px",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  border: `1px solid ${
    score >= 70
      ? "rgba(41, 209, 159, 0.3)"
      : score >= 40
        ? "rgba(41, 209, 159, 0.15)"
        : extraColors.translucent8
  }`,
}))

const CellContainer = styled(Box, {
  shouldForwardProp: prop => prop !== "centered",
})<{ centered?: boolean }>(({ centered }) => ({
  display: "flex",
  alignItems: "center",
  height: "100%",
  width: "100%",
  ...(centered && { justifyContent: "center" }),
}))

const SourceCellBadge = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  gap: "6px",
  backgroundColor: extraColors.translucent5,
  padding: "2px 6px",
  borderRadius: "4px",
  border: `1px solid ${extraColors.dark20}`,
  color: extraColors.white,
}))

const DateLocationTypography = styled(Typography)(() => ({
  ...appTheme.typography.small1Bold,
  opacity: 0.8,
}))

const DiseaseTypography = styled(Typography)(() => ({
  ...appTheme.typography.small1Bold,
  fontWeight: 700,
  opacity: 0.9,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}))

export const DescriptionTypography = styled(Typography)(() => ({
  fontSize: "0.75rem",
  fontWeight: 500,
  color: extraColors.white,
  opacity: 0.9,
  lineHeight: 1.3,
  py: 1,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  textOverflow: "ellipsis",
}))

const getDiseaseValue = (row: BiothreatSignal) => {
  if (row.clean_disease_codes.length > 0) {
    return row.clean_disease_codes.join(", ")
  }
  return row.nwss_meta?.disease ?? ""
}

/**
 * Per-row action icons (Add to Selected Signals · Submit Feedback · Open in
 * Wingman). Shared so the classic grid and the v2 console use the same column.
 */
export const getPrecomputedActionColumn = ({
  onPin,
  pinnedIds,
}: {
  onPin: (row: BiothreatSignal) => void
  pinnedIds: Set<string>
}): GridColDef<BiothreatSignal> => ({
  field: "action",
  headerName: "",
  width: 110,
  sortable: false,
  align: "center",
  headerAlign: "center",
  renderCell: ({ row }) => (
    <Box
      // Keep row-level interactions (e.g. the detail drawer) from firing when
      // a user clicks one of these per-row action buttons.
      onClick={e => e.stopPropagation()}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
        width: "100%",
      }}
    >
      <ActionCell
        actions={[
          {
            onAction: signal => onPin(signal),
            icon: faCirclePlus,
            tooltip: "Add to Selected Signals",
            color: extraColors.status.navyLight,
            shouldShow: signal => !pinnedIds.has(signal.id),
          },
          {
            icon: faCommentDots,
            tooltip: "Submit Feedback",
            color: extraColors.translucent50,
            iconSize: 18,
          },
          {
            icon: faMicroscope,
            tooltip: "Open in Wingman",
            color: extraColors.status.navyLight,
            iconSize: 18,
            to: (signal: BiothreatSignal) => ({
              pathname: ROUTES.WINGMAN,
              search: `?${new URLSearchParams({
                newChatMessage: JSON.stringify({
                  title: signal.title,
                  query: buildBiothreatSignalsContextMessage(
                    signal as AlertCurationBiothreatSignal
                  ),
                } satisfies NewChatMessage),
              })}`,
            }),
          },
        ]}
        row={row}
      />
    </Box>
  ),
})

export const getPrecomputedSignalColumns = ({
  onPin,
  pinnedIds,
  dateColumnHeaderName = "DATE",
  dateColumnDescription,
  dateColumnWidth = 200,
  titleColumnHeaderName = "DESCRIPTION",
}: {
  onPin: (row: BiothreatSignal) => void
  pinnedIds: Set<string>
  dateColumnHeaderName?: string
  dateColumnDescription?: string
  dateColumnWidth?: number
  titleColumnHeaderName?: string
}): GridColDef<BiothreatSignal>[] => [
  getPrecomputedActionColumn({ onPin, pinnedIds }),
  {
    field: "impact_score",
    headerName: "IMPACT",
    flex: 0,
    minWidth: 108,
    width: 108,
    align: "center",
    headerAlign: "center",
    type: "number",
    renderCell: ({ row }) => {
      const score = row.impact_score
      if (score == null) return null
      return (
        <CellContainer centered>
          <ImpactScoreBadge score={score}>{score}</ImpactScoreBadge>
        </CellContainer>
      )
    },
  },
  {
    field: "source",
    headerName: "TYPE",
    width: 72,
    minWidth: 72,
    align: "center",
    headerAlign: "center",
    sortable: false,
    renderCell: ({ row }) => {
      const sourceType = row.source
      const icon = SOURCE_TYPE_TO_ICON[sourceType]
      const label =
        sourceType === "nwss"
          ? PRECOMPUTED_WASTEWATER_SOURCE_LABEL
          : SOURCE_TYPE_TO_LABEL[sourceType]
      return (
        <Tooltip title={label} arrow placement="top">
          <CellContainer centered sx={{ opacity: 0.6 }}>
            <FontAwesomeIcon icon={icon} style={{ fontSize: 18 }} />
          </CellContainer>
        </Tooltip>
      )
    },
  },
  {
    field: "cluster_count",
    headerName: "SOURCE",
    width: 116,
    minWidth: 116,
    type: "number",
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => {
      const clusterIcon = (
        <FontAwesomeIcon
          icon={faLayerGroup}
          style={{ fontSize: 14, opacity: 1 }}
        />
      )
      const members = row.gdelt_meta?.most_recent_members ?? []
      const clusterCount = row.cluster_count
      const badge = (
        <SourceCellBadge sx={{ gap: 0.5 }}>
          {clusterIcon}
          {clusterCount > 0 && (
            <Typography
              variant="small1Bold"
              color="inherit"
              sx={{ lineHeight: "normal" }}
            >
              {clusterCount}
            </Typography>
          )}
        </SourceCellBadge>
      )

      const tooltipProps = {
        arrow: true,
        placement: "right" as const,
        slotProps: {
          tooltip: {
            sx: {
              bgcolor: extraColors.dark,
              border: `1px solid ${extraColors.translucent8}`,
              boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              p: 0,
            },
          },
        },
      }

      if (row.source === "gdelt" && clusterCount > 0) {
        return (
          <CellContainer centered>
            <Tooltip
              {...tooltipProps}
              title={
                <ClusterMembersTooltip
                  clusterCount={clusterCount}
                  members={members}
                />
              }
            >
              {badge}
            </Tooltip>
          </CellContainer>
        )
      }

      if (row.source === "nwss" && row.nwss_meta) {
        return (
          <CellContainer centered>
            <Tooltip
              {...tooltipProps}
              title={
                <NwssSourceTooltip
                  meta={row.nwss_meta}
                  sourceCount={clusterCount}
                />
              }
            >
              {badge}
            </Tooltip>
          </CellContainer>
        )
      }

      return <CellContainer centered>{badge}</CellContainer>
    },
  },
  {
    field: "publish_time",
    headerName: dateColumnHeaderName,
    description: dateColumnDescription,
    flex: 0,
    minWidth: dateColumnWidth,
    width: dateColumnWidth,
    align: "center",
    headerAlign: "center",
    renderCell: ({ row }) => (
      <CellContainer centered>
        <DateLocationTypography>
          {row.publish_time
            ? row.source === "nwss"
              ? formatDateOnly(row.publish_time)
              : formatLocalDateTime(row.publish_time)
            : ""}
        </DateLocationTypography>
      </CellContainer>
    ),
  },
  {
    field: "active_date_range",
    headerName: "ACTIVE DATE RANGE",
    description:
      "GDELT: first cluster member through latest member publish date. Wastewater: event start through last detection.",
    flex: 0,
    minWidth: 230,
    width: 230,
    sortable: false,
    align: "center",
    headerAlign: "center",
    valueGetter: (_, row) => formatBiothreatActiveDateRange(row),
    renderCell: ({ row }) => (
      <CellContainer centered>
        <DateLocationTypography>
          {formatBiothreatActiveDateRange(row)}
        </DateLocationTypography>
      </CellContainer>
    ),
  },
  {
    field: "disease",
    headerName: "DISEASE",
    flex: 0,
    minWidth: 120,
    width: 120,
    align: "left",
    headerAlign: "left",
    valueGetter: (_, row) => getDiseaseValue(row),
    renderCell: ({ row }) => {
      const codes = row.clean_disease_codes
      const rawValue = codes[0] || row.nwss_meta?.disease || ""
      const hasMore = codes.length > 1
      return (
        <CellContainer>
          <DiseaseTypography>
            {rawValue.replace(/_/g, " ")}
            {hasMore && "..."}
          </DiseaseTypography>
        </CellContainer>
      )
    },
  },
  {
    field: "resolved_location",
    headerName: "LOCATION",
    flex: 0,
    minWidth: 128,
    width: 128,
    align: "left",
    headerAlign: "left",
    renderCell: ({ row }) => (
      <CellContainer>
        <DateLocationTypography>
          {row.source === "nwss"
            ? formatNwssResolvedLocation(row.resolved_location)
            : row.resolved_location || ""}
        </DateLocationTypography>
      </CellContainer>
    ),
  },
  {
    field: "title",
    headerName: titleColumnHeaderName,
    flex: 1,
    minWidth: 260,
    align: "left",
    headerAlign: "left",
    renderCell: ({ row }) => {
      const description = precomputedSignalDescription(row)
      const content = (
        <DescriptionTypography>{description}</DescriptionTypography>
      )
      if (!row.url) {
        return <CellContainer>{content}</CellContainer>
      }
      return (
        <Link
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          color="inherit"
          // The title is an explicit link to the article; don't also trigger
          // the row-level detail drawer when it's clicked.
          onClick={e => e.stopPropagation()}
          sx={{
            display: "flex",
            height: "100%",
            width: "100%",
            gap: 1,
            "&:hover": { textDecoration: "underline" },
          }}
        >
          <CellContainer>{content}</CellContainer>
        </Link>
      )
    },
  },
]
